/**
 * LandingProjectSections organism — the home page's Active / Shared /
 * Archived / Deleted layout (LEV-2014 step 4). `LoggedInLandingContent`
 * renders this in place of the flat grid in `LandingProjects.tsx` once the
 * `project-archive` flag is on for the viewer; `LandingProjects.tsx` stays as
 * the flag-off layout, so the two coexist rather than one replacing the other.
 *
 * The pure helpers live in `landingProjectGrouping.ts`, not `landingProjectSections.ts`:
 * a name differing only by case from this file would resolve to the wrong module on a
 * case-insensitive filesystem (macOS, Windows) with no error.
 *
 * Section bars only appear once more than one group holds projects — a
 * founder with only active projects sees today's plain grid, with no
 * vocabulary about sections they've never needed. Once they appear, all four
 * are collapsible bars because a founder with years of projects should not
 * scroll past a wall of tiles to reach the section they want. Active and
 * Shared start open (they are live work); Archived and Deleted start closed.
 * The "New project" card lives inside Active, so collapsing Active hides it.
 *
 * `canRestore` gates ONLY whether the Deleted section can render (it mirrors
 * the `project-hard-delete` flag the caller already checked server-side) — an
 * archived project's Unarchive path is independent of that flag and always
 * renders when the viewer owns an archived project.
 *
 * This component owns every lifecycle confirmation dialog (archive, unarchive,
 * restore, delete, Delete now) once the archive flag is on, including the
 * delete dialog's "Archive instead" off-ramp. Only the `canRestore` delete copy
 * promises destruction after the window: with the flag off nothing purges for
 * that founder, so saying so there would be false.
 *
 * "Delete now"'s own confirmation gates its destructive button on a checkbox
 * (`purgeConfirmed`), reset to unchecked on every open via `openPurgeDialog` —
 * never left ticked across a close/reopen, including for a different project.
 */
"use client";

import React, { useId, useState } from "react";
import {
  ProjectCard,
  type ProjectCardMenuItem,
  type ProjectOwnerSummary,
  ownerDisplayName,
} from "@/components/molecules/projects/ProjectCard";
import { NewProjectCard } from "@/components/molecules/projects/NewProjectCard";
import { Button } from "@/components/atoms/primitives/Button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/ui/Accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/molecules/ui/Dialog";
import { PRODUCT_STEPS } from "@/constants/steps";
import { trackProjectDeleteDialogOpened } from "@/lib/analytics/client";
import {
  PROJECT_PURGE_WINDOW_DAYS,
  type ProjectLifecycleAction,
  type ProjectLifecycleActionOptions,
} from "@/constants/projectLifecycle";
import { useHomeSectionsOpen } from "@/hooks/useHomeSectionsOpen";
import {
  cardRole,
  collaboratorAccessLine,
  formatDaysAgo,
  isPurgePending,
  purgeDaysLeft,
  purgeDaysLeftLabel,
  restoreWindowPhrase,
  sharedWithLine,
  type LandingProjectGroups,
  type LandingSectionKey,
} from "./landingProjectGrouping";
import type { LandingProjectListItem } from "./LandingProjects";

export interface LandingProjectSectionsProps {
  groups: LandingProjectGroups;
  viewerUserId?: string;
  userInitials?: string;
  showNewProjectCard: boolean;
  onNewProjectClick: () => void;
  /** Whether two-stage delete (the `project-hard-delete` flag) is on for this viewer. */
  canRestore: boolean;
  onLifecycleAction: (
    action: ProjectLifecycleAction,
    projectId: string,
    /** `source: "delete_dialog"` records that this archive was chosen as the delete dialog's off-ramp, not the card menu. */
    opts?: ProjectLifecycleActionOptions,
  ) => void | Promise<void>;
  /** Wall-clock reference for every "N days ago" / "N days left" label; tests pass a fixed value. */
  now: number;
}

type LifecycleDialogState =
  | { kind: "archive"; item: LandingProjectListItem }
  | { kind: "unarchive"; item: LandingProjectListItem }
  // `restore` only clears deletedAt — archivedAt survives deleteNow — so an
  // archived-then-deleted project restores back into Archived, not Active.
  | { kind: "restore"; item: LandingProjectListItem }
  | { kind: "delete"; item: LandingProjectListItem }
  // "Delete now" on a Deleted card: the second confirmation before the project
  // is destroyed ahead of the restore window.
  | { kind: "purge"; item: LandingProjectListItem }
  // Read-only: a card whose destruction is already under way has nothing left
  // to confirm or cancel.
  | { kind: "purge-pending"; item: LandingProjectListItem }
  | { kind: "collaborator-archived"; item: LandingProjectListItem }
  | { kind: "collaborator-deleted"; item: LandingProjectListItem }
  | null;

/** First sentence of the delete dialog's body, shared by both `canRestore` branches below. */
const DELETE_DIALOG_SCOPE_SENTENCE =
  "This project holds every document, its links to people in your Orbit, and the AI's memory of your work together.";

/**
 * Delete dialog body: varies by `canRestore` and includes the "Archive
 * instead" steer only when the project is not already archived (an archived
 * card's delete flow has nothing left to steer toward).
 */
function deleteDialogBody(item: LandingProjectListItem, canRestore: boolean): React.ReactNode {
  const collaboratorLine = collaboratorAccessLine(item);
  return (
    <div className="space-y-2">
      <p>
        {canRestore
          ? `${DELETE_DIALOG_SCOPE_SENTENCE} It moves to Deleted, where you can restore it for ${PROJECT_PURGE_WINDOW_DAYS} days. After that it is deleted for good and cannot be brought back.`
          : `${DELETE_DIALOG_SCOPE_SENTENCE} It disappears from your projects, and you will not be able to open it again.`}
      </p>
      {/* Two-stage delete still leaves a collaborator an inert "Deleted by …"
          card until the purge window closes, so "removes it" would overstate
          what happens; "can't open it" is true under both canRestore states. */}
      {collaboratorLine && <p>{`${collaboratorLine} Nobody can open it after that.`}</p>}
      {item.archivedAt == null && (
        <p>
          If you just want it out of the way, archive it instead. Archiving keeps everything
          and stops all email.
        </p>
      )}
    </div>
  );
}

/**
 * "Delete now" dialog body. Names only the project's links to people, never
 * the people: the Person rows in the founder's Orbit survive the purge.
 */
function purgeDialogBody(item: LandingProjectListItem): React.ReactNode {
  const collaboratorLine = collaboratorAccessLine(item);
  return (
    <div className="space-y-2">
      <p>
        {"This skips the rest of the restore window. Every document and the AI's memory of your work go now, "}
        <strong>and you cannot undo this</strong>
        {"."}
      </p>
      <p>{"The people in your Orbit stay. Only this project's links to them go."}</p>
      {collaboratorLine && <p>{`${collaboratorLine} Nobody can open it after this.`}</p>}
    </div>
  );
}

/** Archive dialog body: names collaborators only when the project has any. */
function archiveDialogBody(item: LandingProjectListItem): React.ReactNode {
  const collaboratorLine = collaboratorAccessLine(item);
  return (
    <div className="space-y-2">
      <p>
        Archiving freezes it: no email, no tasks, no edits. Everything stays, and you can
        unarchive it any time.
      </p>
      {collaboratorLine && <p>{`${collaboratorLine} Archiving freezes this project for everyone.`}</p>}
    </div>
  );
}

function currentStepLabelFor(item: LandingProjectListItem): string | undefined {
  const maxStepIndex =
    item.blocks.length > 0 ? Math.max(...item.blocks.map((b) => b.stepIndex)) : -1;
  return maxStepIndex >= 0 ? PRODUCT_STEPS[maxStepIndex]?.label : undefined;
}

const SECTION_GRID_CLASSES = "grid gap-6 sm:grid-cols-2 lg:grid-cols-3";
/** One style for the Active heading and every collapsible bar's label, so the four read as one set. */
const SECTION_HEADING_CLASSES =
  "text-xs font-semibold uppercase tracking-wider text-slate-700";
const SECTION_DESCRIPTION_CLASSES = "text-xs font-normal text-slate-600";

/** Copy under each section heading. No count anywhere: the description replaces it, and the grid is the only indicator of how many. */
const ACTIVE_SECTION_DESCRIPTION = "Projects you're working on now.";
const SHARED_SECTION_DESCRIPTION = "Projects you share and projects shared with you.";
const ARCHIVED_SECTION_DESCRIPTION =
  "Frozen and kept. Unarchive any time to pick up where you left off.";
const DELETED_SECTION_DESCRIPTION = `Restorable for ${PROJECT_PURGE_WINDOW_DAYS} days, then deleted for good.`;

export function LandingProjectSections({
  groups,
  viewerUserId,
  userInitials,
  showNewProjectCard,
  onNewProjectClick,
  canRestore,
  onLifecycleAction,
  now,
}: LandingProjectSectionsProps) {
  const [openKeys, setOpenKeys] = useHomeSectionsOpen(viewerUserId);
  const [dialogState, setDialogState] = useState<LifecycleDialogState>(null);
  // The purge dialog's second confirmation. Reset on every OPEN (never left
  // to persist) so a founder who ticked it, closed the dialog, and reopened
  // it for a different project can never destroy that project unconfirmed.
  const [purgeConfirmed, setPurgeConfirmed] = useState(false);
  const purgeCheckboxId = useId();

  const sharedVisible = groups.shared.length > 0;
  const archivedVisible = groups.archived.length > 0;
  const deletedVisible = canRestore && groups.deleted.length > 0;
  const nonEmptyGroupCount = [
    groups.active.length > 0,
    sharedVisible,
    archivedVisible,
    deletedVisible,
  ].filter(Boolean).length;
  const showHeadings = nonEmptyGroupCount > 1;

  const activeAndSharedEmpty = groups.active.length === 0 && !sharedVisible;
  const showEverythingArchivedPrompt =
    activeAndSharedEmpty && (archivedVisible || deletedVisible);

  const showActiveSection = groups.active.length > 0 || showNewProjectCard;
  const activeCollapsible = showHeadings && groups.active.length > 0;
  const nothingToRender =
    !showActiveSection && !sharedVisible && !archivedVisible && !deletedVisible;
  if (nothingToRender) return null;

  /**
   * Delete… always opens this dialog (never ProjectCard's legacy one); fire
   * `project_delete_dialog_opened` here so every open is counted once,
   * regardless of which card menu (active/shared vs. archived) triggered it.
   * This event is the denominator for `project_archived{source:"delete_dialog"}` —
   * without it there is no rate for how often the steer converts.
   */
  function openDeleteDialog(item: LandingProjectListItem) {
    trackProjectDeleteDialogOpened({
      projectId: item.id,
      canRestore,
      hasCollaborators: item._count.collaborators > 0,
    });
    setDialogState({ kind: "delete", item });
  }

  /** Opens "Delete for good": resets the second-confirmation checkbox so it never carries over from a previously opened/closed purge dialog. */
  function openPurgeDialog(item: LandingProjectListItem) {
    setPurgeConfirmed(false);
    setDialogState({ kind: "purge", item });
  }

  function renderLifecycleCard(item: LandingProjectListItem, index: number, sectionKey: LandingSectionKey) {
    const isOwner = cardRole(item, viewerUserId) === "owner";
    const sharedProps = {
      id: item.id,
      name: item.name,
      updatedAt: item.updatedAt,
      initials: userInitials,
      owner: item.owner,
      isOwnedByViewer: isOwner,
      index,
      currentStepLabel: currentStepLabelFor(item),
    };

    if (sectionKey === "active" || sectionKey === "shared") {
      // Archive and Delete… both open this component's own dialog, never
      // ProjectCard's legacy "You can't undo this" dialog (`onDelete`) — that
      // path is reachable only from the flag-off `LandingProjects.tsx`.
      // `canRestore` changes the delete dialog's copy (restorable vs. gone
      // outright), never which component owns the confirmation.
      const ownerMenuItems: ProjectCardMenuItem[] | undefined = isOwner
        ? [
            {
              key: "archive",
              label: "Archive",
              onSelect: () => setDialogState({ kind: "archive", item }),
            },
            {
              key: "delete",
              label: "Delete…",
              destructive: true,
              onSelect: () => openDeleteDialog(item),
            },
          ]
        : undefined;
      // "Shared with …" is Shared-group-only: an owner's Active card has no
      // collaborators to name, and Archived/Deleted below carry their own
      // chip vocabulary instead of a byline addition.
      const sharedWithLabel =
        sectionKey === "shared" && isOwner ? sharedWithLine(item) ?? undefined : undefined;
      return (
        <ProjectCard
          key={item.id}
          {...sharedProps}
          menuItems={ownerMenuItems}
          sharedWithLabel={sharedWithLabel}
        />
      );
    }

    if (sectionKey === "archived") {
      if (isOwner) {
        const menuItems: ProjectCardMenuItem[] = [
          {
            key: "unarchive",
            label: "Unarchive",
            onSelect: () => onLifecycleAction("unarchive", item.id),
          },
          {
            key: "delete",
            label: "Delete…",
            destructive: true,
            onSelect: () => openDeleteDialog(item),
          },
        ];
        return (
          <ProjectCard
            key={item.id}
            {...sharedProps}
            state="archived"
            chipLabel={`Archived ${formatDaysAgo(item.archivedAt as Date | string, now)}`}
            menuItems={menuItems}
            onInertClick={() => setDialogState({ kind: "unarchive", item })}
          />
        );
      }
      return (
        <ProjectCard
          key={item.id}
          {...sharedProps}
          state="archived"
          chipLabel={`Archived by ${ownerDisplayName(item.owner)}`}
          onInertClick={() => setDialogState({ kind: "collaborator-archived", item })}
        />
      );
    }

    // sectionKey === "deleted". A purge already under way replaces the
    // countdown chip and removes the whole menu: neither Restore nor a second
    // Delete now can be honoured once destruction has been asked for.
    const purgePending = isPurgePending(item);
    if (isOwner) {
      if (purgePending) {
        return (
          <ProjectCard
            key={item.id}
            {...sharedProps}
            state="deleted"
            chipLabel="Deleting now"
            onInertClick={() => setDialogState({ kind: "purge-pending", item })}
          />
        );
      }
      return (
        <ProjectCard
          key={item.id}
          {...sharedProps}
          state="deleted"
          chipLabel={`Deleted · ${purgeDaysLeftLabel(purgeDaysLeft(item.deletedAt as Date | string, now))}`}
          menuItems={[
            {
              key: "restore",
              label: "Restore",
              onSelect: () => onLifecycleAction("restore", item.id),
            },
            {
              key: "purge",
              label: "Delete now",
              destructive: true,
              onSelect: () => openPurgeDialog(item),
            },
          ]}
          onInertClick={() => setDialogState({ kind: "restore", item })}
        />
      );
    }
    return (
      <ProjectCard
        key={item.id}
        {...sharedProps}
        state="deleted"
        chipLabel={purgePending ? "Deleting now" : `Deleted by ${ownerDisplayName(item.owner)}`}
        onInertClick={() => setDialogState({ kind: "collaborator-deleted", item })}
      />
    );
  }

  function closeDialog() {
    setDialogState(null);
  }

  function confirmDialog() {
    if (!dialogState) return;
    if (dialogState.kind === "archive") {
      void onLifecycleAction("archive", dialogState.item.id);
    } else if (dialogState.kind === "unarchive") {
      void onLifecycleAction("unarchive", dialogState.item.id);
    } else if (dialogState.kind === "restore") {
      void onLifecycleAction("restore", dialogState.item.id);
    } else if (dialogState.kind === "delete") {
      void onLifecycleAction("delete", dialogState.item.id);
    } else if (dialogState.kind === "purge") {
      void onLifecycleAction("purge", dialogState.item.id);
    }
    closeDialog();
  }

  /** The delete dialog's "Archive instead" off-ramp — distinct from `confirmDialog`'s "delete" branch, and tagged so the server can tell the two archive entry points apart. */
  function archiveInstead() {
    if (!dialogState || dialogState.kind !== "delete") return;
    void onLifecycleAction("archive", dialogState.item.id, { source: "delete_dialog" });
    closeDialog();
  }

  const activeGrid = (
    <div className={SECTION_GRID_CLASSES}>
      {groups.active.map((item, index) => renderLifecycleCard(item, index, "active"))}
      {showNewProjectCard && <NewProjectCard onClick={onNewProjectClick} />}
    </div>
  );

  return (
    <section className="bg-landing-bg-light py-16 px-4">
      <div className="w-full max-w-[1032px] mx-auto space-y-10">
        {showActiveSection && !activeCollapsible && (
          <div>
            {showEverythingArchivedPrompt && (
              <p className="mb-3 text-sm text-slate-600">
                Everything here is archived or deleted. Start a new idea, or reopen one below.
              </p>
            )}
            {activeGrid}
          </div>
        )}

        {(sharedVisible || archivedVisible || deletedVisible) && (
          // Expand/collapse is a view preference (persisted locally, see
          // useHomeSectionsOpen), not a product action: no PostHog event fires
          // here, matching the analytics-conventions boundary rule.
          <Accordion type="multiple" value={openKeys} onValueChange={setOpenKeys}>
            {activeCollapsible && (
              <AccordionItem value="active">
                <AccordionTrigger className="rounded-lg bg-slate-100 px-4 text-slate-700 hover:bg-slate-200 hover:no-underline">
                  <span className="flex flex-col items-start gap-0.5 text-left">
                    <span className={SECTION_HEADING_CLASSES}>Active projects</span>
                    <span className={SECTION_DESCRIPTION_CLASSES}>
                      {ACTIVE_SECTION_DESCRIPTION}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent>{activeGrid}</AccordionContent>
              </AccordionItem>
            )}
            {sharedVisible && (
              <AccordionItem value="shared">
                <AccordionTrigger className="rounded-lg bg-sky-50 px-4 text-slate-700 hover:bg-sky-100 hover:no-underline">
                  <span className="flex flex-col items-start gap-0.5 text-left">
                    <span className={SECTION_HEADING_CLASSES}>Shared projects</span>
                    <span className={SECTION_DESCRIPTION_CLASSES}>
                      {SHARED_SECTION_DESCRIPTION}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className={SECTION_GRID_CLASSES}>
                    {groups.shared.map((item, index) =>
                      renderLifecycleCard(item, index, "shared"),
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            {archivedVisible && (
              <AccordionItem value="archived">
                <AccordionTrigger className="rounded-lg bg-amber-50 px-4 text-slate-700 hover:bg-amber-100 hover:no-underline">
                  <span className="flex flex-col items-start gap-0.5 text-left">
                    <span className={SECTION_HEADING_CLASSES}>Archived projects</span>
                    <span className={SECTION_DESCRIPTION_CLASSES}>
                      {ARCHIVED_SECTION_DESCRIPTION}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className={SECTION_GRID_CLASSES}>
                    {groups.archived.map((item, index) =>
                      renderLifecycleCard(item, index, "archived"),
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            {deletedVisible && (
              <AccordionItem value="deleted">
                <AccordionTrigger className="rounded-lg bg-rose-50 px-4 text-slate-700 hover:bg-rose-100 hover:no-underline">
                  <span className="flex flex-col items-start gap-0.5 text-left">
                    <span className={SECTION_HEADING_CLASSES}>Deleted projects</span>
                    <span className={SECTION_DESCRIPTION_CLASSES}>
                      {DELETED_SECTION_DESCRIPTION}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className={SECTION_GRID_CLASSES}>
                    {groups.deleted.map((item, index) =>
                      renderLifecycleCard(item, index, "deleted"),
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        )}
      </div>

      <Dialog open={dialogState !== null} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          {dialogState?.kind === "archive" && (
            <>
              <DialogHeader>
                <DialogTitle>{`Archive "${dialogState.item.name}"?`}</DialogTitle>
                <DialogDescription asChild>{archiveDialogBody(dialogState.item)}</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={closeDialog}>
                  Keep active
                </Button>
                <Button type="button" onClick={confirmDialog}>
                  Archive
                </Button>
              </DialogFooter>
            </>
          )}
          {dialogState?.kind === "unarchive" && (
            <>
              <DialogHeader>
                <DialogTitle>{`Unarchive "${dialogState.item.name}"?`}</DialogTitle>
                <DialogDescription>
                  Archived projects are frozen: no email, no tasks, no edits. Unarchive it to
                  pick up where you left off.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={closeDialog}>
                  Keep archived
                </Button>
                <Button type="button" onClick={confirmDialog}>
                  Unarchive
                </Button>
              </DialogFooter>
            </>
          )}
          {dialogState?.kind === "restore" && (
            <>
              <DialogHeader>
                <DialogTitle>{`Restore "${dialogState.item.name}"?`}</DialogTitle>
                <DialogDescription>
                  {`You can restore it for ${restoreWindowPhrase(
                    purgeDaysLeft(dialogState.item.deletedAt as Date | string, now),
                  )}. Restore it to bring it back to your projects.`}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={closeDialog}>
                  Keep deleted
                </Button>
                <Button type="button" onClick={confirmDialog}>
                  Restore
                </Button>
              </DialogFooter>
            </>
          )}
          {dialogState?.kind === "delete" && (
            <>
              <DialogHeader>
                <DialogTitle>{`Delete "${dialogState.item.name}"?`}</DialogTitle>
                <DialogDescription asChild>
                  {deleteDialogBody(dialogState.item, canRestore)}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={closeDialog}>
                  Keep it
                </Button>
                {dialogState.item.archivedAt == null && (
                  <Button type="button" onClick={archiveInstead}>
                    Archive instead
                  </Button>
                )}
                <Button type="button" variant="destructive" onClick={confirmDialog}>
                  Delete
                </Button>
              </DialogFooter>
            </>
          )}
          {dialogState?.kind === "purge" && (
            <>
              <DialogHeader>
                <DialogTitle>{`Delete "${dialogState.item.name}" for good?`}</DialogTitle>
                <DialogDescription asChild>{purgeDialogBody(dialogState.item)}</DialogDescription>
              </DialogHeader>
              <label
                htmlFor={purgeCheckboxId}
                className="flex items-start gap-2 text-sm text-slate-700"
              >
                <input
                  id={purgeCheckboxId}
                  type="checkbox"
                  checked={purgeConfirmed}
                  onChange={(e) => setPurgeConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 cursor-pointer rounded border-slate-300 text-lev-orange focus:ring-lev-orange"
                />
                I understand this cannot be undone
              </label>
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={closeDialog}>
                  Keep it
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={confirmDialog}
                  disabled={!purgeConfirmed}
                >
                  Delete for good
                </Button>
              </DialogFooter>
            </>
          )}
          {dialogState?.kind === "purge-pending" && (
            <>
              <DialogHeader>
                <DialogTitle>This project is being deleted</DialogTitle>
                <DialogDescription>
                  {dialogState.item.purgeRequestedAt != null
                    ? "Deleting it was requested and cannot be undone. It disappears from this list when the deletion finishes."
                    : `Its ${PROJECT_PURGE_WINDOW_DAYS}-day restore window ended and deletion has begun. It cannot be undone and disappears from this list when the deletion finishes.`}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" onClick={closeDialog}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
          {dialogState?.kind === "collaborator-archived" && (
            <>
              <DialogHeader>
                <DialogTitle>{`"${dialogState.item.name}" is archived`}</DialogTitle>
                <DialogDescription>
                  {`${ownerDisplayName(dialogState.item.owner)} archived this project, so it is frozen: nothing can be opened or changed until they unarchive it.`}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" onClick={closeDialog}>
                  OK
                </Button>
              </DialogFooter>
            </>
          )}
          {dialogState?.kind === "collaborator-deleted" && (
            <>
              <DialogHeader>
                <DialogTitle>{`"${dialogState.item.name}" is deleted`}</DialogTitle>
                <DialogDescription>
                  {isPurgePending(dialogState.item)
                    ? dialogState.item.purgeRequestedAt != null
                      ? `${ownerDisplayName(dialogState.item.owner)} is deleting this project for good. It cannot be restored.`
                      : `This project's ${PROJECT_PURGE_WINDOW_DAYS}-day restore window ended and it is being deleted for good. It cannot be restored.`
                    : `${ownerDisplayName(dialogState.item.owner)} deleted this project. They can restore it for ${restoreWindowPhrase(
                        purgeDaysLeft(dialogState.item.deletedAt as Date | string, now),
                      )}.`}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" onClick={closeDialog}>
                  OK
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default LandingProjectSections;
